import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  viewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { filter, finalize, first } from "rxjs/operators";
import {
  CONFIRM_DIALOG_CONFIRM_ACTION,
  CONFIRM_DIALOG_CUSTOMER_DELETE,
} from "../../locale/consts.en";
import { ItemEditComponent } from "../item-edit/item-edit.component";
import { MasterDataService } from "../master-data/master-data-service";
import { Customer } from "../master-data/master-data.model";
import {
  ConfDialogType,
  ConfirmDialogModel,
} from "../shared/confirm-dialog/confim-dialog.model";
import { ConfirmDialogComponent } from "../shared/confirm-dialog/confirm-dialog.component";
import { sortByStringWithEmptyAtEnd } from "../shared/helpers/helper-functions";
import { ChainsCustomersEditComponent } from "./chains-customers-edit/chains-customers-edit.component";
import { Chain, ChangeChainCommand, CreateChainCommand } from "./chains.model";
import { ChainsService } from "./chains.service";

@Component({
  selector: "app-chains",
  templateUrl: "./chains.component.html",
  styleUrls: ["./chains.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChainsComponent implements OnInit, AfterViewInit {
  private chainsService = inject(ChainsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private masterDataService = inject(MasterDataService);
  dialog = inject(MatDialog);

  readonly sort = viewChild(MatSort);
  dataSource: MatTableDataSource<Chain>;
  displayedColumns: string[] = [
    "name",
    "externalId",
    "customers",
    "add",
    "edit",
    "delete",
  ];
  chains: Chain[];
  customers: Customer[];

  ngOnInit(): void {
    const chains = this.route.snapshot.data.chains ?? [];
    this.chains = sortByStringWithEmptyAtEnd(chains, (item) => item.name);
    // this.chains = this.route.snapshot.data.chains.sort((a, b) =>
    //   a.name.localeCompare(b.name)
    // );
    this.dataSource = new MatTableDataSource(this.chains);
    this.masterDataService
      .getCustomers()
      .pipe(filter((cust) => cust != null))
      .subscribe({
        next: (custs) => {
          this.customers = custs.sort((a, b) =>
            a.name > b.name ? 1 : b.name > a.name ? -1 : 0,
          );
        },
        error: (err) => {
          console.log("ERROR when reading customers ", err);
        },
      });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onBack() {
    this.router.navigate(["/"]).then();
  }

  onAddChain() {
    const chain = new Chain();
    const dialogRef = this.dialog.open(ItemEditComponent, {
      width: "20vw",
      data: chain,
    });
    dialogRef.afterClosed().subscribe(async (data: Chain) => {
      if (data != null) {
        data.customers = [];
        this.saveChainToApi(data);
      }
    });
  }

  saveChainToApi(chain: Chain) {
    const cmd: CreateChainCommand = {
      name: chain.name,
      externalId: chain.externalId,
      customerUuids: [],
    };

    this.chainsService
      .createChain(cmd)
      .pipe(
        finalize(() => {
          this.reloadChains();
        }),
      )
      .subscribe({
        next: (response) => {
          this.chainsService.handleResponse(response);
        },
        error: () => console.log("Save ERROR!"),
      });
  }

  private reloadChains() {
    this.chainsService
      .getAllChains()
      .pipe(first())
      .subscribe((chains) => {
        this.chains = sortByStringWithEmptyAtEnd(chains, (item) => item.name);
        // this.chains = chains.sort((a, b) => a.name.localeCompare(b.name));
        this.dataSource.data = this.chains;
      });
  }

  updateChain(chain: Chain) {
    const dialogRef = this.dialog.open(ItemEditComponent, {
      width: "20vw",
      data: chain,
    });
    dialogRef.afterClosed().subscribe(async (data: Chain) => {
      if (data != null) {
        data.customers = chain.customers;
        this.updateChainToApi(data);
      }
    });
  }

  updateChainToApi(chain: Chain) {
    const cmd: ChangeChainCommand = {
      chainUuid: chain.uuid,
      name: chain.name,
      externalId: chain.externalId,
      customerUuids: chain.customers.map((c) => c.uuid),
    };

    this.chainsService
      .updateChain(cmd)
      .pipe(
        finalize(() => {
          this.reloadChains();
        }),
      )
      .subscribe({
        next: (response) => {
          this.chainsService.handleResponse(response);
        },
        error: () => console.log("Save ERROR!"),
      });
  }

  deleteCustomer(chain: Chain, i: number) {
    const dialogData = new ConfirmDialogModel(
      CONFIRM_DIALOG_CONFIRM_ACTION + "  " + chain.name,
      CONFIRM_DIALOG_CUSTOMER_DELETE + "   " + chain.customers[i].name,
      ConfDialogType.CONFIRM,
    );

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        chain.customers.splice(i, 1);
        console.log("Deleting customer", chain);
        this.updateChainToApi(chain);
      }
    });
  }

  addNextCustomer(chain: Chain) {
    const origChain = chain;
    console.log("CUST", this.customers);
    const availableCustomers = this.customers.filter(
      (cust) => chain.customers.findIndex((cs) => cs.uuid === cust.uuid) === -1,
    );
    const dialogRef = this.dialog.open(ChainsCustomersEditComponent, {
      width: "75%",
      data: availableCustomers,
    });
    dialogRef.afterClosed().subscribe((data: Customer) => {
      if (data != null) {
        origChain.customers = [...origChain.customers, data];
        this.updateChainToApi(origChain);
      }
    });
  }

  deleteChain(chain: Chain) {
    console.log("Delete chain by database admin " + chain);
  }
}
